import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Shield, Upload, Trash2, Loader2, CheckCircle2, AlertTriangle,
  FileKey, Eye, EyeOff, Lock, RefreshCw,
  Info
} from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useWorkspace } from '../hooks/useWorkspace'

// ============================================================
// TYPES
// ============================================================

interface CertificatoStatus {
  configured: boolean
  id?: string
  ambiente?: 'sandbox' | 'produzione'
  filename?: string
  sha256?: string
  sizeBytes?: number
  uploadedAt?: string
}

interface CertificatoItem {
  id: string
  ambiente: 'sandbox' | 'produzione'
  filename: string
  sha256: string
  sizeBytes: number
  uploadedBy?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface CertTestResult {
  valid: boolean
  certId?: string
  subject?: string
  issuer?: string
  notBefore?: string
  notAfter?: string
  expiresAt?: string
  error?: string
}

// ============================================================
// COMPONENT
// ============================================================

export default function SidFirma() {
  const maxCertUploadBytes = 50 * 1024
  const { currentWorkspace } = useWorkspace()
  const { language } = useLanguage()
  const queryClient = useQueryClient()

  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [testResult, setTestResult] = useState<CertTestResult | null>(null)
  const [certTestResults, setCertTestResults] = useState<Record<string, CertTestResult>>({})
  const [ambiente, setAmbiente] = useState<'sandbox' | 'produzione'>('produzione')

  // ── QUERY: stato certificato corrente (compat) ──
  const { data: certStatus, isLoading: certLoading } = useQuery<CertificatoStatus>({
    queryKey: ['sid-certificato', currentWorkspace?.id],
    queryFn: async () => {
      const r = await api.get('/sid/certificato')
      return r.data.data
    },
    enabled: !!currentWorkspace,
  })

  // ── QUERY: lista tutti i certificati ──
  const { data: certList } = useQuery<CertificatoItem[]>({
    queryKey: ['sid-certificati', currentWorkspace?.id],
    queryFn: async () => {
      const r = await api.get('/sid/certificati')
      return r.data.data
    },
    enabled: !!currentWorkspace,
  })

  // ── MUTATION: upload certificato ──
  const uploadMutation = useMutation({
    mutationFn: async ({ file, pwd }: { file: File; pwd: string }) => {
      const formData = new FormData()
      formData.append('certificato', file)
      formData.append('password', pwd)
      formData.append('ambiente', ambiente)
      const r = await api.post('/sid/certificato', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return r.data
    },
    onSuccess: (data) => {
      if (data.success) {
        setUploadSuccess(true)
        setUploadError(null)
        setSelectedFile(null)
        setPassword('')
        setTestResult(null)
        setCertTestResults({})
        queryClient.invalidateQueries({ queryKey: ['sid-certificato'] })
        queryClient.invalidateQueries({ queryKey: ['sid-certificati'] })
      } else {
        setUploadError(data.error || 'Errore sconosciuto')
      }
    },
    onError: (err: Error) => {
      setUploadError(err.message)
    },
  })

  // ── MUTATION: elimina certificato (vecchia API compat) ──
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const r = await api.delete('/sid/certificato')
      return r.data
    },
    onSuccess: () => {
      setTestResult(null)
      setCertTestResults({})
      setUploadSuccess(false)
      queryClient.invalidateQueries({ queryKey: ['sid-certificato'] })
      queryClient.invalidateQueries({ queryKey: ['sid-certificati'] })
    },
  })

  // ── MUTATION: elimina certificato per ID ──
  const deleteCertByIdMutation = useMutation({
    mutationFn: async (certId: string) => {
      const r = await api.delete(`/sid/certificati/${certId}`)
      return r.data
    },
    onSuccess: () => {
      setCertTestResults({})
      queryClient.invalidateQueries({ queryKey: ['sid-certificato'] })
      queryClient.invalidateQueries({ queryKey: ['sid-certificati'] })
    },
  })

  // ── MUTATION: attiva certificato ──
  const activateMutation = useMutation({
    mutationFn: async (certId: string) => {
      const r = await api.post(`/sid/certificati/${certId}/activate`)
      return r.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sid-certificato'] })
      queryClient.invalidateQueries({ queryKey: ['sid-certificati'] })
    },
  })

  // ── MUTATION: test certificato ──
  const testMutation = useMutation({
    mutationFn: async (certId?: string) => {
      const r = await api.post('/sid/certificato/test', certId ? { certId } : {})
      return {
        certId: certId || '__active__',
        payload: r.data,
      }
    },
    onSuccess: ({ certId, payload }) => {
      const result: CertTestResult = payload.success && payload.data
        ? {
            ...payload.data,
            certId,
            notAfter: payload.data.notAfter || payload.data.expiresAt || payload.data.expires_at,
          }
        : {
            valid: false,
            certId,
            error: payload.error || 'Test fallito',
          }

      if (certId === '__active__') {
        setTestResult(result)
      } else {
        setCertTestResults((prev) => ({ ...prev, [certId]: result }))
      }
    },
    onError: (err: Error, certId) => {
      const result = { valid: false, certId: certId || '__active__', error: err.message }
      if ((certId || '__active__') === '__active__') {
        setTestResult(result)
      } else {
        setCertTestResults((prev) => ({ ...prev, [certId as string]: result }))
      }
    },
  })

  // ── DROPZONE ──
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setUploadError(null)
    setUploadSuccess(false)
    setTestResult(null)
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      const name = file.name.toLowerCase()
      if (!name.endsWith('.p12') && !name.endsWith('.pfx')) {
        setUploadError(language === 'it'
          ? 'Solo file .p12 o .pfx sono supportati'
          : 'Only .p12 or .pfx files are supported')
        return
      }
      if (file.size > maxCertUploadBytes) {
        setUploadError(language === 'it'
          ? 'File troppo grande (max 50 KB)'
          : 'File too large (max 50 KB)')
        return
      }
      setSelectedFile(file)
    }
  }, [language, maxCertUploadBytes])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/x-pkcs12': ['.p12', '.pfx'] },
    maxFiles: 1,
    multiple: false,
  })

  function handleUpload() {
    if (!selectedFile) return
    if (!password.trim()) {
      setUploadError(language === 'it'
        ? 'Inserisci la password del certificato'
        : 'Enter the certificate password')
      return
    }
    setUploadError(null)
    setUploadSuccess(false)
    uploadMutation.mutate({ file: selectedFile, pwd: password })
  }

  function handleDelete() {
    if (!confirm(language === 'it'
      ? 'Eliminare il certificato? Dovrai caricarne uno nuovo per firmare le comunicazioni.'
      : 'Delete the certificate? You will need to upload a new one to sign communications.')) {
      return
    }
    deleteMutation.mutate()
  }

  const isLoading = uploadMutation.isPending || deleteMutation.isPending || testMutation.isPending || deleteCertByIdMutation.isPending || activateMutation.isPending

  // ── RENDER ──
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-orange-100 dark:bg-orange-900/30">
          <FileKey className="w-6 h-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {language === 'it' ? 'Certificato SID' : 'SID Certificate'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {language === 'it'
              ? 'Carica il certificato .p12 rilasciato dall\'Agenzia delle Entrate per firmare le comunicazioni'
              : 'Upload the .p12 certificate issued by the Revenue Agency to sign communications'}
          </p>
        </div>
      </div>

      {/* Info panel */}
      <div className="flex gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <p className="font-medium">
            {language === 'it' ? 'Come ottenere il certificato?' : 'How to get the certificate?'}
          </p>
          <ol className="list-decimal list-inside space-y-0.5 text-blue-700 dark:text-blue-400">
            {language === 'it' ? (
              <>
                <li>Accedere al portale <strong>Desktop Telematico</strong> dell'Agenzia delle Entrate</li>
                <li>Nella sezione <strong>Sicurezza</strong> → <strong>Gestione ambiente di sicurezza</strong></li>
                <li>Generare la coppia di chiavi e scaricare il certificato <strong>.p12</strong></li>
                <li>Conservare la password scelta in fase di generazione</li>
              </>
            ) : (
              <>
                <li>Access the <strong>Desktop Telematico</strong> portal from the Revenue Agency</li>
                <li>Go to <strong>Security</strong> → <strong>Security environment management</strong></li>
                <li>Generate the key pair and download the <strong>.p12</strong> certificate</li>
                <li>Keep the password you chose during generation</li>
              </>
            )}
          </ol>
        </div>
      </div>

      {/* Current certificate status */}
      {certLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : certStatus?.configured ? (
        <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              <div>
                <p className="font-semibold text-green-900 dark:text-green-200">
                  {language === 'it' ? 'Certificato configurato' : 'Certificate configured'}
                </p>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {certStatus.filename}
                </p>
              </div>
            </div>
            <button
              onClick={handleDelete}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {language === 'it' ? 'Rimuovi' : 'Remove'}
            </button>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="px-3 py-2 rounded-lg bg-white/60 dark:bg-slate-800/50">
              <span className="text-gray-500 dark:text-gray-400 text-xs">SHA-256</span>
              <p className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate">{certStatus.sha256}</p>
            </div>
            <div className="px-3 py-2 rounded-lg bg-white/60 dark:bg-slate-800/50">
              <span className="text-gray-500 dark:text-gray-400 text-xs">
                {language === 'it' ? 'Caricato il' : 'Uploaded on'}
              </span>
              <p className="text-gray-700 dark:text-gray-300">
                {certStatus.uploadedAt
                  ? new Date(certStatus.uploadedAt).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })
                  : '-'}
              </p>
            </div>
            <div className="px-3 py-2 rounded-lg bg-white/60 dark:bg-slate-800/50">
              <span className="text-gray-500 dark:text-gray-400 text-xs">
                {language === 'it' ? 'Dimensione' : 'Size'}
              </span>
              <p className="text-gray-700 dark:text-gray-300">
                {certStatus.sizeBytes ? `${(certStatus.sizeBytes / 1024).toFixed(1)} KB` : '-'}
              </p>
            </div>
            <div className="px-3 py-2 rounded-lg bg-white/60 dark:bg-slate-800/50">
              <span className="text-gray-500 dark:text-gray-400 text-xs">
                {language === 'it' ? 'Formato' : 'Format'}
              </span>
              <p className="text-gray-700 dark:text-gray-300">
                {certStatus.ambiente === 'sandbox'
                  ? (language === 'it' ? 'PKCS#12 · Sandbox' : 'PKCS#12 · Sandbox')
                  : certStatus.ambiente === 'produzione'
                  ? (language === 'it' ? 'PKCS#12 · Produzione' : 'PKCS#12 · Production')
                  : 'PKCS#12'}
              </p>
            </div>
          </div>

          {/* Test button */}
          <button
            onClick={() => testMutation.mutate(undefined)}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors text-sm font-medium"
          >
            {testMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {language === 'it' ? 'Verifica validità certificato' : 'Verify certificate validity'}
          </button>

          {/* Test result */}
          {testResult && (
            <div className={`p-4 rounded-lg border ${testResult.valid
              ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {testResult.valid ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                )}
                <span className={`font-medium ${testResult.valid
                  ? 'text-green-800 dark:text-green-300'
                  : 'text-red-800 dark:text-red-300'
                }`}>
                  {testResult.valid
                    ? (language === 'it' ? 'Certificato valido' : 'Certificate valid')
                    : (language === 'it' ? 'Certificato non valido' : 'Invalid certificate')}
                </span>
              </div>
              {testResult.error && (
                <p className="text-sm text-red-700 dark:text-red-400">{testResult.error}</p>
              )}
              {testResult.subject && (
                <div className="mt-2 space-y-1 text-sm">
                  <p><span className="text-gray-500 dark:text-gray-400">Subject:</span>{' '}
                    <span className="font-mono text-gray-700 dark:text-gray-300">{testResult.subject}</span></p>
                  {testResult.issuer && (
                    <p><span className="text-gray-500 dark:text-gray-400">Issuer:</span>{' '}
                      <span className="font-mono text-gray-700 dark:text-gray-300">{testResult.issuer}</span></p>
                  )}
                  {(testResult.notBefore || testResult.notAfter || testResult.expiresAt) && (
                    <p><span className="text-gray-500 dark:text-gray-400">
                      {language === 'it' ? 'Validità:' : 'Validity:'}</span>{' '}
                      <span className="text-gray-700 dark:text-gray-300">
                        {testResult.notBefore || '—'} — {testResult.notAfter || testResult.expiresAt || '—'}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Replace certificate */}
          <div className="pt-2 border-t border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-400 mb-3">
              {language === 'it'
                ? 'Puoi sostituire il certificato caricandone uno nuovo qui sotto.'
                : 'You can replace the certificate by uploading a new one below.'}
            </p>
          </div>
        </div>
      ) : null}

      {/* Lista certificati caricati */}
      {certList && certList.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-3">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-400" />
            {language === 'it' ? 'Certificati caricati' : 'Uploaded certificates'}
          </h2>
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {certList.map((cert) => (
              <div key={cert.id} className="py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileKey className={`w-5 h-5 flex-shrink-0 ${cert.isActive ? 'text-green-500' : 'text-gray-400'}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {cert.filename}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          cert.ambiente === 'sandbox'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {cert.ambiente === 'sandbox' ? '🧪 Sandbox' : '🔴 Produzione'}
                        </span>
                        {cert.isActive && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {language === 'it' ? 'Attivo' : 'Active'}
                          </span>
                        )}
                        {cert.uploadedBy && (
                          <span className="text-xs text-gray-400 truncate">{cert.uploadedBy}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {language === 'it' ? 'Caricato:' : 'Uploaded:'}{' '}
                        {new Date(cert.createdAt).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => testMutation.mutate(cert.id)}
                      disabled={isLoading}
                      className="px-2 py-1 text-xs rounded-md text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20 transition-colors"
                      title={language === 'it' ? 'Testa certificato' : 'Test certificate'}
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    {!cert.isActive && (
                      <button
                        onClick={() => activateMutation.mutate(cert.id)}
                        disabled={isLoading}
                        className="px-2 py-1 text-xs rounded-md bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/40 transition-colors"
                        title={language === 'it' ? 'Attiva' : 'Activate'}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (confirm(language === 'it'
                          ? `Eliminare il certificato ${cert.filename}?`
                          : `Delete certificate ${cert.filename}?`)) {
                          deleteCertByIdMutation.mutate(cert.id)
                        }
                      }}
                      disabled={isLoading}
                      className="px-2 py-1 text-xs rounded-md text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                      title={language === 'it' ? 'Elimina' : 'Delete'}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {certTestResults[cert.id] && (
                  <div className={`mt-2 ml-8 rounded-lg border p-3 text-xs ${
                    certTestResults[cert.id].valid
                      ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/10 dark:border-green-800 dark:text-green-300'
                      : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/10 dark:border-red-800 dark:text-red-300'
                  }`}>
                    <div className="flex items-center gap-2">
                      {certTestResults[cert.id].valid
                        ? <CheckCircle2 className="w-4 h-4" />
                        : <AlertTriangle className="w-4 h-4" />}
                      <span className="font-medium">
                        {certTestResults[cert.id].valid
                          ? (language === 'it' ? 'Certificato valido' : 'Certificate valid')
                          : (language === 'it' ? 'Certificato non valido' : 'Certificate invalid')}
                      </span>
                    </div>
                    {certTestResults[cert.id].subject && (
                      <p className="mt-1">
                        <span className="text-gray-500 dark:text-gray-400">Subject:</span>{' '}
                        <span className="font-mono">{certTestResults[cert.id].subject}</span>
                      </p>
                    )}
                    {(certTestResults[cert.id].notAfter || certTestResults[cert.id].expiresAt) && (
                      <p className="mt-1">
                        <span className="text-gray-500 dark:text-gray-400">
                          {language === 'it' ? 'Scadenza:' : 'Expires:'}
                        </span>{' '}
                        {certTestResults[cert.id].notAfter || certTestResults[cert.id].expiresAt}
                      </p>
                    )}
                    {certTestResults[cert.id].error && (
                      <p className="mt-1">{certTestResults[cert.id].error}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload area */}
      <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Upload className="w-5 h-5 text-gray-400" />
          {certStatus?.configured
            ? (language === 'it' ? 'Sostituisci certificato' : 'Replace certificate')
            : (language === 'it' ? 'Carica certificato' : 'Upload certificate')}
        </h2>

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`
            flex flex-col items-center justify-center py-8 px-4
            border-2 border-dashed rounded-xl cursor-pointer transition-all
            ${isDragActive
              ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20'
              : selectedFile
                ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
                : 'border-gray-300 dark:border-slate-600 hover:border-orange-300 dark:hover:border-orange-700 hover:bg-gray-50 dark:hover:bg-slate-700/50'
            }
          `}
        >
          <input {...getInputProps()} />
          {selectedFile ? (
            <div className="text-center">
              <FileKey className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedFile.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                {language === 'it' ? 'Clicca o trascina per cambiare file' : 'Click or drag to change file'}
              </p>
            </div>
          ) : (
            <div className="text-center">
              <Lock className="w-10 h-10 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {isDragActive
                  ? (language === 'it' ? 'Rilascia il certificato qui' : 'Drop the certificate here')
                  : (language === 'it'
                    ? 'Trascina qui il certificato .p12 oppure clicca per selezionarlo'
                    : 'Drag the .p12 certificate here or click to select it')}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {language === 'it' ? 'Formati supportati: .p12, .pfx (max 50 KB)' : 'Supported formats: .p12, .pfx (max 50 KB)'}
              </p>
            </div>
          )}
        </div>

        {/* Ambiente selector */}
        {selectedFile && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {language === 'it' ? 'Ambiente' : 'Environment'}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setAmbiente('produzione')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  ambiente === 'produzione'
                    ? 'bg-red-50 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-400'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300 dark:border-slate-600 dark:text-gray-400 dark:hover:border-slate-500'
                }`}
              >
                🔴 {language === 'it' ? 'Produzione' : 'Production'}
              </button>
              <button
                onClick={() => setAmbiente('sandbox')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  ambiente === 'sandbox'
                    ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-400'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300 dark:border-slate-600 dark:text-gray-400 dark:hover:border-slate-500'
                }`}
              >
                🧪 Sandbox
              </button>
            </div>
          </div>
        )}

        {/* Password field */}
        {selectedFile && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {language === 'it' ? 'Password del certificato' : 'Certificate password'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={language === 'it' ? 'Inserisci la password...' : 'Enter password...'}
                className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter') handleUpload() }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {language === 'it'
                ? 'La password usata in fase di generazione del certificato nel Desktop Telematico'
                : 'The password used when generating the certificate in Desktop Telematico'}
            </p>
          </div>
        )}

        {/* Error */}
        {uploadError && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {uploadError}
          </div>
        )}

        {/* Success */}
        {uploadSuccess && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            {language === 'it'
              ? 'Certificato caricato con successo!'
              : 'Certificate uploaded successfully!'}
          </div>
        )}

        {/* Upload button */}
        {selectedFile && (
          <button
            onClick={handleUpload}
            disabled={isLoading || !password.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 dark:disabled:bg-slate-600 text-white font-medium transition-colors text-sm"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {language === 'it' ? 'Carica certificato' : 'Upload certificate'}
          </button>
        )}
      </div>

      {/* Security notice */}
      <div className="flex gap-3 p-4 rounded-xl bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700">
        <Shield className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
          <p className="font-medium text-gray-600 dark:text-gray-300">
            {language === 'it' ? 'Sicurezza' : 'Security'}
          </p>
          <p>
            {language === 'it'
              ? 'Il certificato viene salvato in modo criptato (AES-256 at rest) su Cloudflare R2. La password è conservata separatamente. Solo il tuo workspace può accedere al certificato.'
              : 'The certificate is stored encrypted (AES-256 at rest) on Cloudflare R2. The password is stored separately. Only your workspace can access the certificate.'}
          </p>
        </div>
      </div>
    </div>
  )
}
